import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle2, ScrollText, ChevronDown, Shield, FileText } from 'lucide-react';

const TERMS_SECTIONS = [
  {
    num: '01',
    title: 'Programme Scope',
    color: '#285020',
    clauses: [
      'This Training Programme is offered by Imfuyo as a paid capacity-building service.',
      'The training consists of three sessions: (1) Dairy goat introduction, (2) Deep dive with dairy goat experts, and (3) Real farm visits, as detailed in the programme syllabus provided at registration. Any changes will be communicated 7 days in advance.',
      'Payment grants access to the training only and does not constitute participation in any financing, livestock supply, or investment programme unless separately agreed in writing.',
    ],
  },
  {
    num: '02',
    title: 'Fees & Payment',
    color: '#2563eb',
    clauses: [
      'The training fee is KES 6,000, payable in full prior to participation.',
      'Payment must be made to: MPESA Paybill No: 522522, Account Number: 130-378-73-42. Participants must retain M-Pesa confirmation or bank receipt as proof of payment. Access will be granted within 24 hours of payment confirmation.',
      'Access to the training is only granted upon confirmed payment.',
      'All payments are non-transferable.',
    ],
  },
  {
    num: '03',
    title: 'Refund Policy',
    color: '#dc2626',
    clauses: [
      'Fees paid are non-refundable, except where: (a) the training is cancelled by Imfuyo; or (b) a refund is required under applicable Kenyan consumer protection laws. Refunds, where applicable, will be processed within 30 calendar days of the cancellation notice or refund request approval.',
      'Failure to attend the training does not qualify for a refund.',
      'Participants must attend all three sessions to receive a certificate of completion. Attendance at fewer than three sessions does not entitle the participant to a refund, rescheduling, or certificate.',
    ],
  },
  {
    num: '04',
    title: 'Participant Responsibilities',
    color: '#7c3aed',
    intro: 'Participants agree to:',
    clauses: [
      'Provide accurate registration information.',
      'Attend and participate in the training sessions as scheduled.',
      'Conduct themselves in a professional and respectful manner — including arriving on time, not disrupting sessions, treating trainers and other participants with courtesy, and refraining from discriminatory, abusive, or threatening behaviour. Imfuyo reserves the right to remove participants who violate these standards, with no refund.',
      'Not record, reproduce, or distribute training materials without written consent.',
    ],
  },
  {
    num: '05',
    title: 'Intellectual Property',
    color: '#0284c7',
    clauses: [
      'All training materials, content, and methodologies remain the property of Imfuyo.',
      'Participants are granted a limited, non-exclusive, non-transferable right to use materials for personal learning only. Unauthorized reproduction, distribution, or commercial use of training materials constitutes breach of contract and infringement of intellectual property rights. Imfuyo reserves the right to pursue legal remedies including damages of up to KES 50,000 per breach.',
    ],
  },
  {
    num: '06',
    title: 'Certification',
    color: '#ca8a04',
    clauses: [
      'Upon successful completion of all three sessions, participants will receive a certificate of completion at no additional cost.',
      'Certificates are issued within 14 days of completing the final session.',
    ],
  },
  {
    num: '07',
    title: 'No Guarantee & Limitation of Liability',
    color: '#d97706',
    clauses: [
      'The training is for educational purposes only. Imfuyo does not guarantee financial outcomes, business success, or eligibility for any other programme.',
      'To the fullest extent permitted by law, Imfuyo\'s total liability for any claim arising from the training shall not exceed the amount paid for the training. This limitation applies to all causes of action including breach of contract, negligence, and any other legal theory.',
    ],
  },
  {
    num: '08',
    title: 'Programme Changes',
    color: '#0891b2',
    clauses: [
      'Imfuyo reserves the right to modify training content, format, or schedule. These Terms may be updated from time to time; changes will be posted at www.imfuyo.farm with an effective date. Participants who register and pay before the effective date are bound by the terms in effect at the time of their registration. Material changes will be notified by email 14 days before taking effect.',
      'Material changes include: changes to training dates by more than 7 days; changes to training location by more than 20 kilometres; removal of any of the three core sessions; or changes to trainer qualifications. Non-material changes (e.g., venue within the same area or minor content updates) do not require advance notice.',
    ],
  },
  {
    num: '09',
    title: 'Cancellation & Rescheduling',
    color: '#be185d',
    clauses: [
      'Imfuyo reserves the right to cancel or postpone the training. Cancellation means failure to deliver all three scheduled training sessions. If one or two sessions are delivered but the third is cancelled, Imfuyo will either: (a) reschedule the remaining session(s) within 30 days, or (b) provide a pro-rata refund.',
      'In cases of cancellation by Imfuyo, participants shall be offered: (a) a rescheduled session; or (b) a full or applicable refund. Refunds will be processed within 30 calendar days of the cancellation notice or refund request approval.',
      'Participants who voluntarily withdraw after attending one or more sessions are not entitled to any refund. By attending the first session, participants confirm they have reviewed the training content and wish to proceed.',
    ],
  },
  {
    num: '10',
    title: 'Data Protection',
    color: '#285020',
    clauses: [
      'Participant data shall be collected and processed in accordance with the Data Protection Act, 2019 (Kenya).',
      'Data will only be used for programme administration and communication related to the training.',
      'Participant data will be retained for 5 years from the date of training completion for record-keeping and audit purposes, after which it will be securely deleted unless required by law to be retained longer.',
    ],
  },
  {
    num: '11',
    title: 'Force Majeure',
    color: '#64748b',
    clauses: [
      'Imfuyo shall not be liable for failure to deliver training due to circumstances beyond reasonable control including natural disasters, government action, civil unrest, or public health emergencies.',
      'In such cases, Imfuyo will reschedule the training at no additional cost.',
    ],
  },
  {
    num: '12',
    title: 'Dispute Resolution',
    color: '#7c3aed',
    clauses: [
      'Disputes or complaints should be submitted in writing to hello@imfuyo.farm and copied to bernardnjathi@gmail.com. Imfuyo will acknowledge receipt within 5 business days and respond within 14 days.',
      'Any disputes not resolved amicably shall be referred to arbitration in Nairobi or the nearest arbitration center to the participant\'s county of residence, at the participant\'s choice, in accordance with the Arbitration Act, 1995 (Kenya).',
    ],
  },
  {
    num: '13',
    title: 'Governing Law',
    color: '#1d4ed8',
    clauses: [
      'These Terms shall be governed by the laws of Kenya.',
    ],
  },
  {
    num: '14',
    title: 'Severability',
    color: '#059669',
    clauses: [
      'If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.',
    ],
  },
  {
    num: '15',
    title: 'Acceptance',
    color: '#285020',
    clauses: [
      'By registering and paying for the training, the participant confirms acceptance of these Terms & Conditions.',
      'Participants must tick a checkbox stating: "I have read, understood, and agree to the Terms and Conditions." Imfuyo shall store the timestamp and IP address of acceptance.',
      'Electronic acceptance constitutes a legally binding agreement under the Kenya Information and Communications Act.',
    ],
  },
  {
    num: '16',
    title: 'Entire Agreement',
    color: '#475569',
    clauses: [
      'These Terms and Conditions, together with any signed programme participation agreements, constitute the entire agreement between the participant and Imfuyo regarding the MbuziPlus Training Programme and any subsequent participation in the MbuziPlus livestock programme. This agreement supersedes all prior or contemporaneous understandings, agreements, representations, warranties, and communications, both written and oral.',
      'Participation in the training programme does not guarantee, imply, or create any obligation for Imfuyo to include the participant in the MbuziPlus livestock programme, financing arrangements, or any other Imfuyo programmes. Acceptance into the MbuziPlus livestock programme is subject to separate eligibility criteria, financing approval, and execution of additional binding agreements.',
      'No oral statements, promotional materials, social media posts, informal communications, or third-party representations shall form part of this agreement or create any obligations on Imfuyo beyond those expressly stated herein.',
      'The participant acknowledges that any terms, conditions, rights, or obligations not expressly included in this written agreement are deemed waived and cannot be enforced against Imfuyo. If any provision is ambiguous or incomplete, it shall be interpreted in a manner consistent with Imfuyo\'s reasonable business practices and applicable Kenyan law.',
      'No modification, amendment, or waiver of any provision of these Terms shall be effective unless in writing and signed by an authorized representative of Imfuyo. The failure of Imfuyo to enforce any right or provision in these Terms shall not constitute a waiver of such right or provision.',
    ],
  },
];

/* ─── TERMS MODAL ─────────────────────────────────────────── */
export function TermsModal({ onClose, onAccept, isDark, alreadyAccepted = false }) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const scrollRef = useRef(null);

  const cardBg      = isDark ? '#1f2937' : '#fff';
  const textMain    = isDark ? '#f9fafb' : '#111827';
  const textSub     = isDark ? '#9ca3af' : '#6b7280';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const sectionBg   = isDark ? '#111827' : '#f9fafb';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const handleScroll = (e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) setHasScrolledToBottom(true);
  };

  const canAccept = hasScrolledToBottom || alreadyAccepted;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }} />

      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: '680px',
          maxHeight: '92vh', borderRadius: '28px 28px 0 0',
          background: cardBg, boxShadow: '0 -24px 80px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column', margin: '0 auto',
          animation: 'fadeUp 0.3s ease',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem 1rem',
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
          background: isDark
            ? 'linear-gradient(135deg, #14301A, #1f2937)'
            : 'linear-gradient(135deg, #F1F5EE, #fff)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '42px', height: '42px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #285020, #3E7A32)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(22,163,74,0.3)',
              }}>
                <ScrollText size={20} color="#fff" />
              </div>
              <div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#285020', marginBottom: '2px' }}>
                  MbuziPlus · Imfuyo
                </div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: '1.3rem', color: textMain, margin: 0 }}>
                  Terms & Conditions
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: isDark ? '#374151' : '#f3f4f6', border: 'none', cursor: 'pointer', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSub, flexShrink: 0 }}
            >
              <X size={18} />
            </button>
          </div>

          {!hasScrolledToBottom && !alreadyAccepted && (
            <div style={{
              marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: isDark ? '#14301A' : '#E3EEDD',
              border: `1px solid ${isDark ? '#14301A' : '#D5E8CB'}`,
              borderRadius: '8px', padding: '0.5rem 0.85rem',
            }}>
              <ChevronDown size={13} color="#285020" style={{ animation: 'bounce 1.5s ease infinite' }} />
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: isDark ? '#B7D9A8' : '#14301A', fontWeight: 600 }}>
                Scroll to the bottom to enable acceptance
              </span>
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{ overflowY: 'auto', flex: 1, padding: '1.5rem' }}
        >
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: textSub, lineHeight: 1.75, marginBottom: '1.75rem', padding: '0.9rem 1rem', background: isDark ? '#14301A' : '#F1F5EE', borderRadius: '10px', border: `1px solid ${isDark ? '#14301A' : '#D5E8CB'}` }}>
            These Terms & Conditions govern your participation in the <strong style={{ color: '#285020' }}>MbuziPlus Training Programme</strong> offered by Imfuyo. By registering and paying for the training, you confirm acceptance of these terms in full.
          </p>

          {TERMS_SECTIONS.map((section) => (
            <div key={section.num} style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'flex-start' }}>
                {/* Number badge */}
                <div style={{
                  flexShrink: 0, width: '36px', height: '36px', borderRadius: '10px',
                  background: `${section.color}15`,
                  border: `1.5px solid ${section.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: '2px',
                }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.7rem', color: section.color }}>
                    {section.num}
                  </span>
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontFamily: "'Playfair Display', serif", fontWeight: 700,
                    fontSize: '1rem', color: textMain,
                    margin: '0 0 0.6rem', lineHeight: 1.3,
                  }}>
                    {section.title}
                  </h3>

                  {section.intro && (
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: textSub, margin: '0 0 0.5rem', fontStyle: 'italic' }}>
                      {section.intro}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {section.clauses.map((clause, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                          padding: '0.6rem 0.85rem',
                          background: sectionBg,
                          borderRadius: '8px',
                          border: `1px solid ${borderColor}`,
                        }}
                      >
                        <div style={{
                          flexShrink: 0, width: '18px', height: '18px',
                          borderRadius: '50%',
                          background: `${section.color}15`,
                          border: `1.5px solid ${section.color}35`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          marginTop: '2px',
                        }}>
                          <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.55rem', color: section.color }}>
                            {String(i + 1)}
                          </span>
                        </div>
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.84rem', color: textSub, lineHeight: 1.7 }}>
                          {clause}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Divider except last */}
              {section.num !== '16' && (
                <div style={{ height: '1px', background: borderColor, margin: '1.25rem 0 0' }} />
              )}
            </div>
          ))}

          {/* Acceptance clause */}
          <div style={{
            background: isDark ? 'linear-gradient(135deg,#14301A,#111827)' : 'linear-gradient(135deg,#F1F5EE,#fafffe)',
            border: `2px solid ${isDark ? '#14301A' : '#285020'}40`,
            borderRadius: '14px',
            padding: '1.1rem 1.25rem',
            marginTop: '0.5rem',
          }}>
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
              <Shield size={18} color="#285020" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: isDark ? '#B7D9A8' : '#14301A', margin: 0, lineHeight: 1.75, fontWeight: 500 }}>
                <strong>Clause 15 — Acceptance:</strong> By registering and paying for the training, the participant confirms they have read, understood, and accepted these Terms & Conditions in full. Electronic acceptance constitutes a legally binding agreement under the Kenya Information and Communications Act.
              </p>
            </div>
          </div>
        </div>

        {/* Footer — acceptance */}
        <div style={{
          padding: '1.1rem 1.5rem',
          borderTop: `1px solid ${borderColor}`,
          flexShrink: 0,
          background: cardBg,
        }}>
          {!alreadyAccepted && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: canAccept ? 'pointer' : 'not-allowed', marginBottom: '0.85rem', opacity: canAccept ? 1 : 0.45, transition: 'opacity 0.3s' }}>
              <div
                onClick={() => canAccept && setAccepted(a => !a)}
                style={{
                  flexShrink: 0, width: '22px', height: '22px', borderRadius: '6px',
                  border: `2px solid ${accepted ? '#285020' : (isDark ? '#4b5563' : '#d1d5db')}`,
                  background: accepted ? '#285020' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: '1px', transition: 'all 0.18s',
                }}
              >
                {accepted && <CheckCircle2 size={14} color="#fff" strokeWidth={3} />}
              </div>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: textSub, lineHeight: 1.65 }}>
                I have read and understood the MbuziPlus Terms & Conditions and agree to be bound by them.
              </span>
            </label>
          )}

          <button
            onClick={() => {
              if (alreadyAccepted) { onClose(); return; }
              if (accepted && canAccept) onAccept();
            }}
            disabled={!alreadyAccepted && (!accepted || !canAccept)}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.95rem',
              borderRadius: '10px', border: 'none',
              background: (alreadyAccepted || (accepted && canAccept))
                ? 'linear-gradient(135deg,#285020,#3E7A32)'
                : (isDark ? '#374151' : '#e5e7eb'),
              color: (alreadyAccepted || (accepted && canAccept)) ? '#fff' : '#9ca3af',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.95rem',
              cursor: (alreadyAccepted || (accepted && canAccept)) ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              boxShadow: (alreadyAccepted || (accepted && canAccept)) ? '0 4px 20px rgba(22,163,74,0.3)' : 'none',
            }}
          >
            {alreadyAccepted
              ? <><FileText size={16} /> Close Terms</>
              : <><CheckCircle2 size={16} /> I Accept These Terms</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── TERMS BADGE (inline, shows in registration success view) ── */
export function TermsBadge({ onOpen, accepted, isDark }) {
  const textSub = isDark ? '#9ca3af' : '#6b7280';
  return (
    <button
      onClick={onOpen}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
        background: accepted
          ? (isDark ? '#14301A' : '#F1F5EE')
          : (isDark ? '#1f2937' : '#f9fafb'),
        border: `1.5px solid ${accepted ? '#16a34a40' : (isDark ? '#374151' : '#e5e7eb')}`,
        borderRadius: '99px',
        padding: '0.4rem 0.9rem',
        cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.78rem',
        fontWeight: 600,
        color: accepted ? '#285020' : textSub,
        transition: 'all 0.18s',
      }}
    >
      {accepted
        ? <><CheckCircle2 size={13} /> Terms accepted</>
        : <><ScrollText size={13} /> Read Terms & Conditions</>
      }
    </button>
  );
}

export default TermsModal;